import { Spinner } from '@components/spinner';
import { BasePath } from '@constants/Auth';
import { t, Trans } from '@lingui/macro';
import { Box, Button, Card, Grid, makeStyles, Switch, Typography } from '@material-ui/core';
import { orange } from '@material-ui/core/colors';
import { SafeAny } from '@models/common';
import { BillingStatus, BillingType, Company, ECompanyStatus, SettingTabs } from '@models/settings';
import {
  useCreateSubscription,
  useGetCompanySubscriptions,
  useGetSubscriptionConfirm,
  useGetSubscriptions,
  useRedirectStripeCustomerPostal
} from '@queries/settings';
import { useStripe } from '@stripe/react-stripe-js';
import { colors } from '@swyft/ui';
import { useAppContext } from '@utils/appState';
import { hexToRgbA } from '@utils/color';
import { formatCurrency } from '@utils/string';
import { differenceInDays, format, isValid } from 'date-fns';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { StringParam, useQueryParam } from 'use-query-params';
import AlertSection from './AlertSection';

const features = [
  t`5,000 responses per month`,
  t`Unlimited questions & logic jumps`,
  t`Accept payments`,
  t`Multiple outcomes`,
  t`Price impact based on answers`,
  t`Google Tag Manager`,
  t`Email notifications`,
  t`Custom branding`
];

export const successUrl = `${window.location.origin}${BasePath}/settings?activeTab=${SettingTabs.Billing}&successUrl=true`;
export const cancelUrl = `${window.location.origin}${BasePath}/settings?activeTab=${SettingTabs.Billing}&successUrl=false`;

const Billing = () => {
  const classes = useStyles();
  const stripe = useStripe();
  const [successParam, setQuery] = useQueryParam('successUrl', StringParam);
  const [billingType, setBillingType] = React.useState<BillingType>(BillingType.Annually);
  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [isLoadingSetPayment, setIsLoadingPayment] = React.useState<boolean>(false);

  const {
    state: {
      companyInformation: { companyInfo = {} as Company }
    }
  } = useAppContext();

  const { status: companyStatus, trialEnd } = companyInfo;
  const remainTrialDay = React.useMemo(() => {
    if (trialEnd && isValid(new Date(trialEnd))) {
      const diffDays = differenceInDays(new Date(trialEnd), new Date());
      return Math.max(diffDays, 0);
    }
    return 0;
  }, [trialEnd]);

  const { isIdle, data = {}, isLoading } = useGetCompanySubscriptions();
  const { status: billingStatus } = data || {};

  const { data: subscriptions, isLoading: isLoadingGetSubscription } = useGetSubscriptions();

  const currentPeriodEnd = data?.currentPeriodEnd || '';
  const periodMonthlyEndDate = isValid(new Date(currentPeriodEnd)) ? format(new Date(currentPeriodEnd), 'do') : '';

  const periodAnnuallyEndDate = isValid(new Date(currentPeriodEnd))
    ? format(new Date(currentPeriodEnd), 'dd MMMM yyyy')
    : '';

  const monthlyAmount = subscriptions?.find((item: SafeAny) => item.paymentType === BillingType.Monthly)?.amount || 0;

  const annuallyAmount = subscriptions?.find((item: SafeAny) => item.paymentType === BillingType.Annually)?.amount || 0;

  const annuallyPerMonth = Math.round(annuallyAmount / 12);

  const { mutateAsync: createSubscription } = useCreateSubscription();
  const { mutateAsync: redirectStripeCustomerPostal } = useRedirectStripeCustomerPostal();
  const { mutateAsync: getSubscriptionConfirm, isLoading: isLoadingGetSubscriptionConfirm } =
    useGetSubscriptionConfirm();

  React.useEffect(() => {
    if (successParam === 'true') {
      getSubscriptionConfirm();
      setQuery(undefined);
    }
    if (successParam === 'false') setError(true);
  }, []);

  useEffect(() => {
    if (!data) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
      if (data.paymentType) setBillingType(data.paymentType);
    }
  }, [data]);

  if (!stripe || !companyInfo || isIdle || isLoading || isLoadingGetSubscription || isLoadingGetSubscriptionConfirm) {
    return <Spinner />;
  }

  const handleChangeSwitch = () => {
    if (billingType === BillingType.Annually) setBillingType(BillingType.Monthly);
    else setBillingType(BillingType.Annually);
  };

  const handleSetupPayment = async (paymentType: BillingType) => {
    try {
      setIsLoadingPayment(true);
      const subscriptionId = subscriptions?.find((item) => item.paymentType === paymentType)?.id || '';
      const payload = {
        companyId: companyInfo.id,
        subscriptionId,
        successUrl,
        cancelUrl
      };
      const checkoutResponse = await createSubscription(payload);
      if (checkoutResponse && stripe) {
        const result = await stripe.redirectToCheckout({
          sessionId: checkoutResponse?.stripeSessionId
        });
        if (result.error) {
          toast.error(t`Unable to raise payment, please try again`);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const viewBillingDetail = async () => {
    const dataBillingPage = await redirectStripeCustomerPostal({
      companyId: companyInfo.id,
      successUrl: successUrl
    });
    if (dataBillingPage) window.location.href = dataBillingPage;
  };

  return (
    <Card>
      <Box display='flex' justifyContent='space-between' alignItems='center' paddingX={3} paddingY={2}>
        <Grid container>
          <Grid xs={12} item>
            <Typography variant='body2' className='uppercase font-semibold'>
              <Trans>Billing</Trans>
            </Typography>
          </Grid>
          {(companyStatus === ECompanyStatus.Trial ||
            (companyStatus === ECompanyStatus.Inactive && remainTrialDay === 0)) && (
            <Box
              bgcolor={remainTrialDay > 0 ? colors.info.light : hexToRgbA(orange[500], 0.12)}
              className='text-center p-4 my-2 rounded w-full'
            >
              {remainTrialDay > 0 && (
                <Typography variant='body2'>
                  <Trans>You have</Trans>
                  <span className='mx-1'>{remainTrialDay}</span>
                  <Trans> days left in your trial. </Trans>
                </Typography>
              )}
              {remainTrialDay === 0 && (
                <Typography variant='body2'>
                  <Trans>You have</Trans>
                  <span className='mx-1'>{remainTrialDay}</span>
                  <Trans>
                    days left in your trial. Please set up payment to continue generating leads for your business
                  </Trans>
                </Typography>
              )}
            </Box>
          )}
          {error && (
            <Grid xs={12} item className='my-3'>
              <AlertSection
                closeAlert={() => {
                  setError(false);
                  setQuery(undefined);
                }}
              />
            </Grid>
          )}
          {billingStatus === BillingStatus.Cancelled && (
            <Box bgcolor={orange[200]} textAlign='center' p={1} my={2} className='rounded' width='100%'>
              <Trans>Your subscription has been deactivated,</Trans>
              <br />
              <Trans>please reactivate your subscription to continue to generate leads for your business</Trans>
            </Box>
          )}
          <Grid container justifyContent='center' alignItems='center'>
            <Typography variant='h5' className='my-6'>
              <Trans>Choose your plan</Trans>
            </Typography>
            <Grid item xs={12}>
              <Box display='flex' justifyContent='center' alignItems='center'>
                <Typography variant='body2' className={`${billingType === BillingType.Monthly ? 'text-gray-400' : ''}`}>
                  <Trans>Billed Annually</Trans>
                </Typography>
                <Switch
                  checked={billingType === BillingType.Monthly}
                  onChange={handleChangeSwitch}
                  disabled={!isEdit || isLoadingSetPayment}
                  className={classes.customSwitch}
                />
                <Typography
                  variant='body2'
                  className={`${billingType === BillingType.Annually ? 'text-gray-400' : ''}`}
                >
                  <Trans>Billed Monthly</Trans>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography color='error' variant='body2' align='center' className='font-semibold'>
                <Trans>Save</Trans>
                <span className='mx-1'>{formatCurrency(monthlyAmount * 12 - annuallyAmount, 'US')}</span>
                <Trans>on an annual plan!</Trans>
              </Typography>
            </Grid>
            <Grid item xs={8} lg={7} xl={6} container className='mt-12 mb-10 mx-auto p-6 border rounded relative'>
              <Typography
                variant='body2'
                className='font-semibold px-6 absolute top-0 left-2/4 transform -translate-x-2/4 -translate-y-2/4 bg-white'
              >
                <Trans>Your Subscription</Trans>
              </Typography>
              <Grid item xs={12} className='my-4'>
                <Typography variant='h6' className='font-bold' align='center'>
                  <Trans>Essential Package</Trans>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box alignItems='center'>
                  {billingType === BillingType.Monthly && (
                    <Box textAlign='center'>
                      <Typography variant='h6' display='inline' className='font-normal mr-1'>
                        {formatCurrency(monthlyAmount, 'US')}
                      </Typography>
                      <Typography variant='subtitle1' display='inline'>
                        <Trans> per month</Trans>
                      </Typography>
                    </Box>
                  )}
                  {billingType === BillingType.Annually && (
                    <>
                      <Box textAlign='center'>
                        <Typography variant='h6' display='inline' className='font-normal mr-2 line-through'>
                          {formatCurrency(monthlyAmount, 'US')}
                        </Typography>
                        <Typography variant='h6' display='inline' className='font-normal mr-1'>
                          {formatCurrency(annuallyPerMonth, 'US')}
                        </Typography>
                        <Typography variant='subtitle1' display='inline'>
                          <Trans>per month</Trans>
                        </Typography>
                      </Box>

                      <Box display='flex' justifyContent='center'>
                        <Typography variant='caption'>
                          <Trans>Bill at</Trans>
                          <span className='mx-1 line-through'>{formatCurrency(monthlyAmount * 12, 'US')}</span>
                          <span className='mr-1'>{formatCurrency(annuallyAmount, 'US')}</span>
                          <Trans>annually</Trans>
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid xs={10} lg={9} xl={8} item container className='mx-auto my-8'>
                {features.map((item, index) => (
                  <Grid xs={12} item key={index} className='flex mt-2'>
                    <img src='assets/check-icon.svg' />
                    <Typography variant='caption' className='ml-2 font-semibold'>
                      {item}
                    </Typography>
                  </Grid>
                ))}
                {!data && (
                  <Button
                    color='primary'
                    variant='contained'
                    fullWidth
                    className='mt-8 p-3 text-base capitalize font-thin tracking-normal'
                    onClick={() => handleSetupPayment(billingType)}
                    disabled={isLoadingSetPayment}
                  >
                    <Trans>Set up payment</Trans>
                  </Button>
                )}
                {(data?.paymentType === BillingType.Monthly || billingStatus === BillingStatus.Cancelled) && (
                  <Button
                    color='primary'
                    variant='contained'
                    fullWidth
                    className='mt-8 p-3 text-base capitalize font-thin tracking-normal'
                    onClick={() => viewBillingDetail()}
                    disabled={isLoadingSetPayment}
                  >
                    {data?.paymentType === BillingType.Monthly && billingStatus === BillingStatus.Active && (
                      <Trans>Change to Annual Subscription</Trans>
                    )}
                    {billingStatus === BillingStatus.Cancelled && <Trans>Reactivate Subscription</Trans>}
                  </Button>
                )}
              </Grid>
            </Grid>
            {data && billingStatus === BillingStatus.Active && (
              <Grid item xs={12}>
                <Grid
                  xs={6}
                  item
                  container
                  className='mb-12 mx-auto p-6 border rounded text-center'
                  justifyContent='center'
                >
                  {data.paymentType === BillingType.Monthly && (
                    <Typography variant='body2'>
                      You will be billed on the
                      <span className='font-semibold mx-2'>{periodMonthlyEndDate}</span>
                      monthly
                    </Typography>
                  )}
                  {data.paymentType === BillingType.Annually && (
                    <Typography variant='body2'>
                      Plan expires on the
                      <span className='font-semibold ml-2'>{periodAnnuallyEndDate}</span>
                    </Typography>
                  )}
                  <Grid xs={12} item>
                    <Button color='primary' className='capitalize tracking-normal' onClick={() => viewBillingDetail()}>
                      Change / Update Billing Details
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Billing;

const useStyles = makeStyles({
  customSwitch: {
    '& .MuiSwitch-track': {
      backgroundColor: `${colors.primary.main}!important`,
      opacity: '1!important'
    }
  }
});
